import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useIsRevelioAdmin } from '@revelio/auth';
import { Button, FormField, Input, Text } from '@revelio/ui';

import type { SnowflakeDeliveryConfig } from '../../data-builder.store';

type SnowflakeConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValue: SnowflakeDeliveryConfig | null;
  defaultDatabase: string | null;
  defaultSchema: string | null;
  onSave: (value: SnowflakeDeliveryConfig) => void;
};

type SnowflakeConfigForm = {
  database: string;
  schema: string;
  table: string;
};

const validNamePattern = /^[A-Za-z_][A-Za-z0-9_$]*$/;

export const SnowflakeConfigModal = ({
  isOpen,
  onClose,
  initialValue,
  defaultDatabase,
  defaultSchema,
  onSave,
}: SnowflakeConfigModalProps) => {
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SnowflakeConfigForm>({
    defaultValues: {
      database: initialValue?.database ?? defaultDatabase ?? '',
      schema: initialValue?.schema ?? defaultSchema ?? '',
      table: initialValue?.table ?? '',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      database: initialValue?.database ?? defaultDatabase ?? '',
      schema: initialValue?.schema ?? defaultSchema ?? '',
      table: initialValue?.table ?? '',
    });
  }, [defaultDatabase, defaultSchema, initialValue, isOpen, reset]);

  const onSubmit = handleSubmit((value) => {
    const database = isRevelioAdmin ? value.database : (defaultDatabase ?? '');
    const schema = isRevelioAdmin ? value.schema : (defaultSchema ?? '');
    onSave({
      database,
      schema,
      table: value.table,
    });
    onClose();
  });

  const showAdminFields = isRevelioAdmin;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">
          Snowflake Database Configuration
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!showAdminFields && (
            <Text fontSize="sm" style={{ marginBottom: '16px' }}>
              Database and schema are pre-configured for your account.
            </Text>
          )}
          {showAdminFields && (
            <>
              <FormField
                label="Database"
                errors={
                  errors.database?.message !== undefined
                    ? [errors.database.message]
                    : undefined
                }
                style={{ marginBottom: '16px' }}
              >
                <Input
                  placeholder="e.g. database"
                  {...register('database', {
                    required: 'Database is required',
                    pattern: {
                      value: validNamePattern,
                      message:
                        'Names can only contain letters, numbers, underscores and dollar signs.',
                    },
                  })}
                />
              </FormField>

              <FormField
                label="Schema"
                errors={
                  errors.schema?.message !== undefined
                    ? [errors.schema.message]
                    : undefined
                }
                style={{ marginBottom: '16px' }}
              >
                <Input
                  placeholder="e.g. schema"
                  {...register('schema', {
                    required: 'Schema is required',
                    pattern: {
                      value: validNamePattern,
                      message:
                        'Names can only contain letters, numbers, underscores and dollar signs.',
                    },
                  })}
                />
              </FormField>
            </>
          )}

          <FormField
            label="Table"
            errors={
              errors.table?.message !== undefined
                ? [errors.table.message]
                : undefined
            }
          >
            <Input
              placeholder="e.g. my_table"
              {...register('table', {
                required: 'Table is required',
                pattern: {
                  value: validNamePattern,
                  message:
                    'Names can only contain letters, numbers, underscores and dollar signs.',
                },
              })}
            />
          </FormField>
        </ModalBody>

        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={onSubmit} disabled={isSubmitting}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
